import { useState } from "react";
import { NewProjectFormData, ValidationErrors } from "../../types";
import { isNotEmpty } from "../../utils/formatters";
import { apiService, ApiError } from "../api";
import { useToast } from "../../contexts/ToastContext";

/**
 * Custom Hook für Projekt-Formular-Management
 * Kapselt Form State, Validation und Submission-Logik
 */
export const useProjectForm = () => {
  const { showError } = useToast();

  // Form State
  const [formData, setFormData] = useState<NewProjectFormData>({
    name: "",
  });

  // Validation State
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handles input changes for form fields
   */
  const handleInputChange = (field: keyof NewProjectFormData, value: any) => {
    setFormData((prev: NewProjectFormData) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: ValidationErrors) => ({ ...prev, [field]: "" }));
    }
  };

  /**
   * Validates the entire form
   */
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!isNotEmpty(formData.name)) {
      newErrors.name = "Projektname ist erforderlich";
    }

    setErrors(newErrors);
    setShowValidation(true);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submits the form data to the API using apiService with JWT authentication
   *
   * 2-Schritt-Prozess:
   * 1. Projekt erstellen (ohne Flächen-Daten)
   * 2. Area aktualisieren (mit Flächen-Daten)
   *
   * @param onSuccess - Callback-Funktion bei erfolgreichem Speichern
   * @param geometrie - Optional: GeoJSON-Geometrie
   */
  const submitForm = async (
    onSuccess?: (data: any) => void,
    geometrie?: any,
    koordinate?: { breitengrad?: number | null; laengengrad?: number | null },
  ) => {
    if (!validateForm()) {
      return false;
    }

    setIsSubmitting(true);

    try {
      const projectData = {
        name: formData.name,
      };

      const savedProject = await apiService.createProject(projectData);

      const hasGeometrie = !!geometrie;
      const hasKoordinate =
        koordinate?.breitengrad != null || koordinate?.laengengrad != null;

      if (hasGeometrie || hasKoordinate) {
        const areaData: any = {};
        if (hasGeometrie) areaData.geometrie = geometrie;
        if (koordinate?.breitengrad != null)
          areaData.breitengrad = koordinate.breitengrad;
        if (koordinate?.laengengrad != null)
          areaData.laengengrad = koordinate.laengengrad;

        try {
          await apiService.updateProjectArea(savedProject.id, areaData);
        } catch {}
      }

      if (onSuccess) {
        onSuccess(savedProject);
      }

      return true;
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 403) {
        showError("Fehlende Berechtigung: Du darfst keine Projekte anlegen.");
      } else {
        showError(
          "Projekt konnte nicht angelegt werden. Bitte versuche es erneut.",
        );
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    errors,
    showValidation,
    isSubmitting,
    handleInputChange,
    validateForm,
    submitForm,
  };
};
